// src/utils/smsUtils.js
import { toShortRefFront } from "./format";
import { getDisplayName } from "./displayName";

/**
 * Gjetter servicetype basert på produkttype/kategori
 * Brukes til å matche SMS-maler mot "sykkel", "ski", osv.
 */
export function inferWorkshopServiceType(produkt = {}) {
  const t =
    (produkt.produkttype ||
      produkt.type ||
      produkt.kategori ||
      produkt.category ||
      "").toLowerCase();

  if (t.includes("ski")) return "ski";
  if (t.includes("sykkel") || t.includes("bike") || t.includes("cycle"))
    return "sykkel";
  return "verksted";
}

/**
 * Finn "kategori" for servicen, brukt til å filtrere SMS-maler
 * F.eks. "sykkel", "ski", "verksted"
 */
export function getServiceCategory(svc, produkt) {
  // 1) Prøv å lese kategori direkte fra service hvis den finnes
  const fromSvc = (
    svc?.kategori ||
    svc?.category ||
    svc?.serviceCategory ||
    svc?.serviceType ||
    ""
  )
    .toString()
    .trim()
    .toLowerCase();

  if (fromSvc) return fromSvc; // f.eks. "sykkel", "ski", "verksted"

  // 2) Fallback: bruk produkttype/kategori
  const fromProd = (
    produkt?.type ||
    produkt?.kategori ||
    produkt?.produkttype ||
    produkt?.category ||
    ""
  )
    .toString()
    .trim()
    .toLowerCase();

  if (!fromProd) return "";

  // Normaliser til noen få kategorier hvis du vil
  if (fromProd.includes("ski")) return "ski";
  if (fromProd.includes("sykkel")) return "sykkel";

  // Hvis du har andre verdier (f.eks. "verksted", "service" osv)
  return fromProd;
}

/**
 * Fyller inn en SMS-mal med info fra service/kunde.
 * Støtter flere forskjellige feltnavn på mal.
 */
export function fillWorkshopSmsTemplate(
  template,
  { customer, svc, produkt, customerLink }
) {
  if (!template) return "";

  const body = template.tekst || template.body || template.text || "";
  if (!body) return "";

  const shortRef = toShortRefFront(svc?.serviceRef);
  const ref = shortRef || svc?.serviceRef || "";
  const statusText = svc?.status || "";
  const storeName = produkt?.butikk || getDisplayName();
  const link = customerLink || "";

  let text = body;

  const replacements = {
    // Ingen navn i meldinger til kunder (navnet kan være feilstavet eller
    // ufullstendig), så «Hei {navn},» blir «Hei,».
    "{navn}": "",
    "{kundenavn}": "",
    "{kunde}": "",

    // referanse / servicenummer
    "{ref}": ref,
    "{referanse}": ref,
    "%servicenummer%": ref,
    "%serviceid%": ref,
    "%serviceId%": ref,

    // status
    "{status}": statusText,

    // lenke
    "{lenke}": link,
    "{link}": link,

    // butikk
    "{butikk}": storeName,
    "%butikk%": storeName,
  };

  Object.entries(replacements).forEach(([token, value]) => {
    text = text.split(token).join(value || "");
  });

  return text.replace(/Hei\s+([!,])/, "Hei$1").trim();
}

/**
 * Forsøker å velge "riktig" SMS-mal ut fra status/kategori
 */
export function inferWorkshopSmsTemplate(templates, svc, produkt) {
  if (!Array.isArray(templates) || !templates.length) return null;

  const status = (svc?.status || "").toLowerCase();
  const serviceType = inferWorkshopServiceType(produkt);

  // 1) Match på status mot template.type
  const byStatus = templates.find(
    (t) => (t.type || "").toLowerCase() === status
  );
  if (byStatus) return byStatus;

  // 2) (valgfritt) annen logikk
  const byType = templates.find((t) => {
    const cat = (t.serviceType || t.category || "").toLowerCase();
    return cat && cat.includes(serviceType);
  });
  if (byType) return byType;

  // 3) Fallback: første mal
  return templates[0];
}
